import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  School,
  Group,
  Topic,
  Add,
  Remove,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AttendeeInfo {
  name: string;
  position: string;
  signature: string;
  present: boolean;
}

interface SafetyTopic {
  title: string;
  description: string;
  keyPoints: string[];
}

interface ToolboxTalkFormData {
  projectId: string;
  title: string;
  date: string;
  time: string;
  location: string;
  conductor: string;
  duration: number;
  category: string;
  topics: SafetyTopic[];
  hazardsDiscussed: string[];
  safetyReminders: string[];
  attendees: AttendeeInfo[];
  weatherConditions: string;
  workActivities: string[];
  actionItems: string[];
  nextMeetingDate: string;
  notes: string;
  status: string;
}

interface Project {
  _id: string;
  name: string;
}

const ToolboxTalkCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [formData, setFormData] = useState<ToolboxTalkFormData>({
    projectId: '',
    title: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    location: '',
    conductor: user?.firstName + ' ' + user?.lastName || '',
    duration: 15,
    category: 'general-safety',
    topics: [],
    hazardsDiscussed: [],
    safetyReminders: [],
    attendees: [],
    weatherConditions: 'clear',
    workActivities: [],
    actionItems: [],
    nextMeetingDate: '',
    notes: '',
    status: 'scheduled',
  });

  const [errors, setErrors] = useState<any>({});
  const [newHazard, setNewHazard] = useState('');
  const [newReminder, setNewReminder] = useState('');
  const [newActivity, setNewActivity] = useState('');
  const [newActionItem, setNewActionItem] = useState('');

  const categoryOptions = [
    { value: 'general-safety', label: 'General Safety', color: 'primary' },
    { value: 'fall-protection', label: 'Fall Protection', color: 'error' },
    { value: 'electrical-safety', label: 'Electrical Safety', color: 'warning' },
    { value: 'equipment-safety', label: 'Equipment Safety', color: 'info' },
    { value: 'hazmat', label: 'Hazardous Materials', color: 'error' },
    { value: 'ppe', label: 'Personal Protective Equipment', color: 'success' },
    { value: 'fire-safety', label: 'Fire Safety', color: 'error' },
    { value: 'confined-space', label: 'Confined Space', color: 'warning' },
    { value: 'excavation', label: 'Excavation Safety', color: 'warning' },
    { value: 'weather-related', label: 'Weather Related', color: 'info' },
    { value: 'emergency-procedures', label: 'Emergency Procedures', color: 'error' },
    { value: 'other', label: 'Other', color: 'default' },
  ];

  const weatherOptions = [
    { value: 'clear', label: 'Clear' },
    { value: 'cloudy', label: 'Cloudy' },
    { value: 'rainy', label: 'Rainy' },
    { value: 'snowy', label: 'Snowy' },
    { value: 'windy', label: 'Windy' },
    { value: 'foggy', label: 'Foggy' },
    { value: 'extreme-heat', label: 'Extreme Heat' },
    { value: 'extreme-cold', label: 'Extreme Cold' },
  ];

  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.conductor.trim()) {
      newErrors.conductor = 'Conductor is required';
    }

    if (formData.duration <= 0) {
      newErrors.duration = 'Duration must be greater than 0';
    }

    if (formData.topics.length === 0) {
      newErrors.topics = 'At least one safety topic is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ToolboxTalkFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'number' ? parseFloat(event.target.value) || 0 : event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleSelectChange = (field: keyof ToolboxTalkFormData) => (
    event: any
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const addTopic = () => {
    setFormData({
      ...formData,
      topics: [
        ...formData.topics,
        { title: '', description: '', keyPoints: [] }
      ]
    });
  };

  const removeTopic = (index: number) => {
    const updatedTopics = formData.topics.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      topics: updatedTopics,
    });
  };

  const handleTopicChange = (index: number, field: keyof SafetyTopic, value: string | string[]) => {
    const updatedTopics = [...formData.topics];
    updatedTopics[index] = {
      ...updatedTopics[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      topics: updatedTopics,
    });
  };

  const addAttendee = () => {
    setFormData({
      ...formData,
      attendees: [
        ...formData.attendees,
        { name: '', position: '', signature: '', present: true }
      ]
    });
  };

  const removeAttendee = (index: number) => {
    const updatedAttendees = formData.attendees.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      attendees: updatedAttendees,
    });
  };

  const handleAttendeeChange = (index: number, field: keyof AttendeeInfo, value: string | boolean) => {
    const updatedAttendees = [...formData.attendees];
    updatedAttendees[index] = {
      ...updatedAttendees[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      attendees: updatedAttendees,
    });
  };

  const addListItem = (listType: 'hazardsDiscussed' | 'safetyReminders' | 'workActivities' | 'actionItems', value: string) => {
    if (value.trim()) {
      setFormData({
        ...formData,
        [listType]: [...formData[listType], value.trim()]
      });
      
      // Clear the input
      switch (listType) {
        case 'hazardsDiscussed':
          setNewHazard('');
          break;
        case 'safetyReminders':
          setNewReminder('');
          break;
        case 'workActivities':
          setNewActivity('');
          break;
        case 'actionItems':
          setNewActionItem('');
          break;
      }
    }
  };

  const removeListItem = (listType: 'hazardsDiscussed' | 'safetyReminders' | 'workActivities' | 'actionItems', index: number) => {
    const updatedList = formData[listType].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [listType]: updatedList,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}/toolbox-talks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          createdBy: user?._id,
        }),
      });

      if (!response.ok) {
        // Try to extract backend error details
        let backendMessage = 'Failed to create toolbox talk';
        try {
          const data = await response.json();
          if (data) {
            backendMessage = data.message || data.error || backendMessage;
            if (Array.isArray(data.errors) && data.errors.length > 0) {
              backendMessage = `${backendMessage}: ${data.errors.join('; ')}`;
            }
          }
        } catch {
          // Fallback to plain text if not JSON
          try {
            const text = await response.text();
            if (text) backendMessage = text;
          } catch {}
        }
        setError(`${response.status} ${backendMessage}`);
        return;
      }

      const payload = await response.json();
      const created = payload?.data ?? payload;
      const newId = created?._id ?? created?.id;
      setSuccess(true);
      
      // Redirect to toolbox talk detail page after a short delay
      setTimeout(() => {
        if (newId) {
          navigate(`/toolbox-talks/${newId}`);
        } else {
          navigate('/toolbox-talks');
        }
      }, 1500);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/toolbox-talks');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Create Toolbox Talk
        </Typography>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Toolbox talk created successfully! Redirecting to talk details...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Basic Information */}
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <School color="primary" />
                Basic Information
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200, flex: 1 }} error={!!errors.projectId}>
                  <InputLabel>Project *</InputLabel>
                  <Select
                    value={formData.projectId}
                    label="Project *"
                    onChange={handleSelectChange('projectId')}
                    disabled={loadingProjects}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project._id} value={project._id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.projectId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.projectId}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="Title"
                  value={formData.title}
                  onChange={handleInputChange('title')}
                  error={!!errors.title}
                  helperText={errors.title}
                  sx={{ minWidth: 300, flex: 1 }}
                  required
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange('date')}
                  error={!!errors.date}
                  helperText={errors.date}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ minWidth: 200 }}
                  required
                />

                <TextField
                  label="Time"
                  type="time"
                  value={formData.time}
                  onChange={handleInputChange('time')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ minWidth: 150 }}
                />

                <TextField
                  label="Duration (minutes)"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange('duration')}
                  error={!!errors.duration}
                  helperText={errors.duration}
                  sx={{ minWidth: 150 }}
                  inputProps={{
                    min: 1,
                    max: 480,
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Location"
                  value={formData.location}
                  onChange={handleInputChange('location')}
                  error={!!errors.location}
                  helperText={errors.location}
                  sx={{ minWidth: 200, flex: 1 }}
                  required
                />

                <TextField
                  label="Conductor"
                  value={formData.conductor}
                  onChange={handleInputChange('conductor')}
                  error={!!errors.conductor}
                  helperText={errors.conductor}
                  sx={{ minWidth: 200 }}
                  required
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={handleSelectChange('category')}
                  >
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={option.label}
                            color={option.color as any}
                            size="small"
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Weather</InputLabel>
                  <Select
                    value={formData.weatherConditions}
                    label="Weather"
                    onChange={handleSelectChange('weatherConditions')}
                  >
                    {weatherOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={handleSelectChange('status')}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Safety Topics */}
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
                <Topic color="primary" />
                Safety Topics
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1">Discussion Topics</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={addTopic}
                >
                  Add Topic
                </Button>
              </Box>

              {errors.topics && (
                <Alert severity="error">
                  {errors.topics}
                </Alert>
              )}

              {formData.topics.map((topic, index) => (
                <Card key={index} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">Topic {index + 1}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeTopic(index)}
                      sx={{ color: 'black' }}
                    >
                      <Remove />
                    </IconButton>
                  </Box>
                  
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Topic Title"
                      value={topic.title}
                      onChange={(e) => handleTopicChange(index, 'title', e.target.value)}
                      required
                    />
                    
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      value={topic.description}
                      onChange={(e) => handleTopicChange(index, 'description', e.target.value)}
                      placeholder="Describe the safety topic in detail..."
                    />
                    
                    <TextField
                      fullWidth
                      label="Key Points (comma-separated)"
                      value={topic.keyPoints.join(', ')}
                      onChange={(e) => handleTopicChange(index, 'keyPoints', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                      placeholder="Key point 1, Key point 2, Key point 3..."
                      helperText="Separate multiple key points with commas"
                    />
                  </Stack>
                </Card>
              ))}

              {/* Hazards and Safety */}
              <Typography variant="h6" sx={{ mt: 3 }}>Hazards & Safety Reminders</Typography>
              
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Hazards Discussed</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Add Hazard"
                    value={newHazard}
                    onChange={(e) => setNewHazard(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addListItem('hazardsDiscussed', newHazard);
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => addListItem('hazardsDiscussed', newHazard)}
                    disabled={!newHazard.trim()}
                  >
                    Add
                  </Button>
                </Box>
                
                <List dense>
                  {formData.hazardsDiscussed.map((hazard, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={hazard} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeListItem('hazardsDiscussed', index)}
                          sx={{ color: 'black' }}
                        >
                          <Remove />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Safety Reminders</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Add Safety Reminder"
                    value={newReminder}
                    onChange={(e) => setNewReminder(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addListItem('safetyReminders', newReminder);
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => addListItem('safetyReminders', newReminder)}
                    disabled={!newReminder.trim()}
                  >
                    Add
                  </Button>
                </Box>
                
                <List dense>
                  {formData.safetyReminders.map((reminder, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={reminder} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeListItem('safetyReminders', index)}
                          sx={{ color: 'black' }}
                        >
                          <Remove />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Work Activities */}
              <Typography variant="h6" sx={{ mt: 3 }}>Work Activities</Typography>
              
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Add Work Activity"
                    value={newActivity}
                    onChange={(e) => setNewActivity(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addListItem('workActivities', newActivity);
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => addListItem('workActivities', newActivity)}
                    disabled={!newActivity.trim()}
                  >
                    Add
                  </Button>
                </Box>
                
                <List dense>
                  {formData.workActivities.map((activity, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={activity} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeListItem('workActivities', index)}
                          sx={{ color: 'black' }}
                        >
                          <Remove />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Attendees */}
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
                <Group color="primary" />
                Attendees
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1">Meeting Attendees</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={addAttendee}
                >
                  Add Attendee
                </Button>
              </Box>

              {formData.attendees.map((attendee, index) => (
                <Card key={index} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" />
                      Attendee {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeAttendee(index)}
                      sx={{ color: 'black' }}
                    >
                      <Remove />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField
                      label="Name"
                      value={attendee.name}
                      onChange={(e) => handleAttendeeChange(index, 'name', e.target.value)}
                      sx={{ minWidth: 200, flex: 1 }}
                    />
                    
                    <TextField
                      label="Position"
                      value={attendee.position}
                      onChange={(e) => handleAttendeeChange(index, 'position', e.target.value)}
                      sx={{ minWidth: 150 }}
                    />
                    
                    <TextField
                      label="Signature/Initials"
                      value={attendee.signature}
                      onChange={(e) => handleAttendeeChange(index, 'signature', e.target.value)}
                      sx={{ minWidth: 150 }}
                    />
                  </Box>
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={attendee.present}
                        onChange={(e) => handleAttendeeChange(index, 'present', e.target.checked)}
                      />
                    }
                    label="Present at meeting"
                  />
                </Card>
              ))}

              {/* Action Items */}
              <Typography variant="h6" sx={{ mt: 3 }}>Action Items & Follow-up</Typography>
              
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Action Items</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Add Action Item"
                    value={newActionItem}
                    onChange={(e) => setNewActionItem(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addListItem('actionItems', newActionItem);
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => addListItem('actionItems', newActionItem)}
                    disabled={!newActionItem.trim()}
                  >
                    Add
                  </Button>
                </Box>
                
                <List dense>
                  {formData.actionItems.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={item} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeListItem('actionItems', index)}
                          sx={{ color: 'black' }}
                        >
                          <Remove />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>

              <TextField
                label="Next Meeting Date"
                type="date"
                value={formData.nextMeetingDate}
                onChange={handleInputChange('nextMeetingDate')}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ maxWidth: 200 }}
              />

              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                placeholder="Any additional notes or comments about the toolbox talk..."
              />

              {/* Submit Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Creating...' : 'Create Toolbox Talk'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ToolboxTalkCreate;